"use client";

import { Author } from "@/components/Author";
import { Categories } from "@/components/Categories";
import { PortableText } from "next-sanity";
import { POST_BY_SLUG_QUERYResult } from "@/sanity/types";
import { PublishedAt } from "@/components/PublishedAt";
import { Title } from "@/components/Title";
import { urlFor } from "@/sanity/lib/image";
import Image from "next/image";
import { ImageSlide } from "@/components/ImageSlide";
import { useTheme } from "@/contexts/ThemeContext";

/* caption Image type extracted from the TypeGen Result*/
type CaptionImage = NonNullable<
  Extract<
    NonNullable<NonNullable<POST_BY_SLUG_QUERYResult>["content"]>[number],
    { _type: "captionImageGroup" }
  >["images"]
>[number];

export function Post(props: NonNullable<POST_BY_SLUG_QUERYResult>) {
  const { title, author, categories, content, publishedAt, language } = props;
  const { themeColors } = useTheme();
  return (
    <article className={`flex flex-col gap-y-12 bg-transparent`}>
      <header className="flex flex-col gap-4 items-start">
        <div className="flex gap-4 items-center">
          <Categories categories={categories} />
          <PublishedAt publishedAt={publishedAt} />
        </div>
        <Title language={language ?? "en"}>{title}</Title>
        <Author author={author} />
      </header>
      {/* {mainImage ? (
        <figure className="rounded-lg overflow-hidden lg:col-span-12 flex flex-col gap-2 items-start h-[400px] w-full relative">
          <Image
            src={urlFor(mainImage).width(800).height(400).url()}
            fill
            className="object-cover"
            alt=""
          />
        </figure>
      ) : null} */}
      {content ? (
        <div
          className={`site-content-block col-span-12 flex flex-col prose text-medium md:text-large ${language === "kr" ? "lang-kr" : ""}`}
        >
          <PortableText
            value={content}
            components={{
              list: {
                bullet: ({ children }) => (
                  <ul
                    className={`py-1 not-prose font-lyon text-medium md:text-large ${themeColors.paragraphTextColor} ${language === "kr" ? "lang-kr" : ""} w-full max-w-[720px] list-disc pl-6 space-y-2 marker:text-neutral-400`}
                  >
                    {children}
                  </ul>
                ),
                number: ({ children }) => (
                  <ol
                    className={`not-prose font-lyon ${themeColors.paragraphTextColor} text-medium md:text-large ${language === "kr" ? "lang-kr" : ""} w-full max-w-[720px] list-decimal pl-6 space-y-2 marker:text-neutral-400`}
                  >
                    {children}
                  </ol>
                ),
              },
              // Fix for React child error: custom listItem renderer
              listItem: {
                bullet: ({ children }) => (
                  <li className={`not-prose ${themeColors.paragraphTextColor}`}>
                    {children}
                  </li>
                ),
                number: ({ children }) => (
                  <li className={`not-prose ${themeColors.paragraphTextColor}`}>
                    {children}
                  </li>
                ),
              },
              block: ({ value }) => {
                return (
                  <div
                    className={`w-full max-w-[720px] ${themeColors.paragraphTextColor} ${themeColors.paragraphTextOtherColor}`}
                  >
                    <PortableText value={[value]} />
                  </div>
                );
              },
              types: {
                image: ({ value }) => {
                  return (
                    <div className="max-w-[720px] relative h-auto my-[16px]">
                      <Image
                        src={urlFor(value).width(720).url()}
                        alt={value.alt ?? ""}
                        className="rounded-lg object-contain not-prose"
                        fill
                      />
                    </div>
                  );
                },
                captionImageGroup4: ({ value }) => {
                  return (
                    <div className="flex flex-wrap gap-[16px] mb-[1.6em] mt-[1.25em] md:mt-[1.25em] md:mb-[80px]">
                      {Array.isArray(value.images) &&
                      value.images.length > 0 ? (
                        <div className="grid grid-cols-2 gap-x-[3vw] gap-y-[max(1.5em,3vw)] md:grid-cols-4 w-full">
                          {value.images.map(
                            (img: CaptionImage, idx: number) => (
                              <figure
                                key={idx}
                                className="flex flex-col items-start col-span-1 not-prose gap-[1em]"
                              >
                                {img.image?.asset && (
                                  <div
                                    className="relative w-full h-auto"
                                    style={{
                                      aspectRatio:
                                        img.metadata?.dimensions?.aspectRatio ??
                                        undefined,
                                    }}
                                  >
                                    <Image
                                      src={urlFor(img.image).width(768).url()}
                                      alt={
                                        img.title ||
                                        img.description ||
                                        img.location ||
                                        title ||
                                        ""
                                      }
                                      className="rounded-[20px] object-contain not-prose"
                                      fill
                                    />
                                  </div>
                                )}
                                {(img.title ||
                                  img.description ||
                                  img.location) && (
                                  <figcaption
                                    className={`text-small md:text-medium flex flex-col gap-[1em] ${language === "kr" ? "lang-kr" : ""}`}
                                  >
                                    {img.title && <div>{img.title}</div>}
                                    {img.description && (
                                      <div className="text-dark-grey opacity-70">
                                        {img.description}
                                      </div>
                                    )}
                                    {img.location && (
                                      <div className="text-xsmall opacity-70 text-dark-grey wrap-anywhere">
                                        {img.link ? (
                                          <a
                                            href={img.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline"
                                          >
                                            {img.location}
                                          </a>
                                        ) : (
                                          img.location
                                        )}
                                      </div>
                                    )}
                                  </figcaption>
                                )}
                              </figure>
                            )
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                },
                captionImageGroup: ({ value }) => {
                  let className = `grid grid-cols-1 gap-[20px] w-full`;
                  if (value.images.length === 2) {
                    className += " md:grid-cols-2";
                  } else {
                    className += " md:grid-cols-3";
                  }
                  return (
                    <div className="flex flex-wrap gap-[16px] mb-[1.6em] mt-[1.25em] md:mt-[1.25em] md:mb-[80px]">
                      {value.groupTitle ? (
                        <a
                          href={value.groupTitleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <h2
                            className={`text-medium md:text-large not-prose ${language === "kr" ? "lang-kr" : ""}`}
                          >
                            {value.groupTitle}
                          </h2>
                        </a>
                      ) : null}
                      {Array.isArray(value.images) &&
                      value.images.length > 0 ? (
                        <div className={className}>
                          {value.images.map(
                            (img: CaptionImage, idx: number) => (
                              <figure
                                key={idx}
                                className="flex flex-col items-start col-span-1 not-prose gap-[1em]"
                              >
                                {img.image?.asset && (
                                  <div
                                    className="relative w-full h-auto"
                                    style={{
                                      aspectRatio:
                                        img.metadata?.dimensions?.aspectRatio ??
                                        undefined,
                                    }}
                                  >
                                    <Image
                                      src={urlFor(img.image).width(1280).url()}
                                      alt={
                                        img.title ||
                                        img.description ||
                                        img.location ||
                                        title ||
                                        ""
                                      }
                                      className="rounded-[20px] object-contain not-prose"
                                      fill
                                    />
                                  </div>
                                )}
                                {(img.title ||
                                  img.description ||
                                  img.location) && (
                                  <figcaption
                                    className={`text-small md:text-medium flex flex-col gap-[1em] ${language === "kr" ? "lang-kr" : ""}`}
                                  >
                                    {img.title && <div>{img.title}</div>}
                                    {img.description && (
                                      <div className="text-dark-grey opacity-70">
                                        {img.description}
                                      </div>
                                    )}
                                    {img.location && (
                                      <div className="text-xsmall opacity-70 text-dark-grey wrap-anywhere">
                                        {img.link ? (
                                          <a
                                            href={img.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline"
                                          >
                                            {img.location}
                                          </a>
                                        ) : (
                                          img.location
                                        )}
                                      </div>
                                    )}
                                  </figcaption>
                                )}
                              </figure>
                            )
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                },
                imageSlide: ({ value }) => {
                  return <ImageSlide value={value} />;
                },
              },
            }}
          />
        </div>
      ) : null}
      {/* {body ? (
        <div className="site-content-block lg:col-span-12 prose text-medium md:text-large">
          <PortableText value={body} components={components} />
        </div>
      ) : null} */}
    </article>
  );
}
